import type { ShipmentListType } from 'schemas/shipment/inbound/list';
import type { WmsShipmentInboundType } from 'schemas/shipment/inbound/detail';
import { calculateListTotal } from '@page-components/shipments/common';
import {
  canMarkShipmentArrived,
  getInboundWorkflowStepIndex,
  getShipmentItemStatusMeta,
  getShipmentStatusMeta,
  normalizeShipmentStatus,
  type ShipmentStatusKey,
} from '@utility/constants/shipment-status';

export const INBOUND_WORKFLOW_STEPS = [
  'Arrival',
  'Item Count',
  'Storage Assignment',
  'Complete',
] as const;

export const INBOUND_SHIPMENTS_LIST = '/app/shipments/inbound';

export function inboundShipmentDetailPath(id: string) {
  return `${INBOUND_SHIPMENTS_LIST}/${id}`;
}

export interface InboundListDisplay {
  customerName: string;
  supplierName: string;
  supplierCountry: string;
  warehouseName: string;
  warehouseCountry: string;
  cartons: number;
  units: number;
  grossWeight: number;
}

export function resolveInboundListDisplay(row: ShipmentListType): InboundListDisplay {
  const itemTotal = calculateListTotal(
    (row.items ?? []) as Parameters<typeof calculateListTotal>[0],
  );
  const componentTotal = calculateListTotal(
    (row.components ?? []) as Parameters<typeof calculateListTotal>[0],
  );
  const origin = row.origins?.[0];

  return {
    customerName: row.organization?.company_name || '—',
    supplierName: origin?.name || '—',
    supplierCountry: origin?.address?.country || 'US',
    warehouseName: row.destination?.name || '—',
    warehouseCountry: row.destination?.address?.country || 'US',
    cartons: itemTotal.cartons + componentTotal.cartons,
    units: itemTotal.units + componentTotal.units,
    grossWeight: itemTotal.grossWeight + componentTotal.grossWeight,
  };
}

export function getListProductImages(
  items: ShipmentListType['items'],
  limit = 3,
): string[] {
  return (items ?? [])
    .map(i => i.p_images?.display_image)
    .filter((url): url is string => Boolean(url))
    .slice(0, limit);
}

export interface InboundDetailDisplay {
  customerName: string;
  supplierName: string;
  supplierCountry: string;
  warehouseName: string;
  warehouseCountry: string;
}

export function resolveInboundDetailDisplay(
  data: WmsShipmentInboundType,
): InboundDetailDisplay {
  return {
    customerName: data.organization?.company_name || data.customer?.company_name || '—',
    supplierName: data.origin?.name || '—',
    supplierCountry: data.origin?.address?.country || 'US',
    warehouseName: data.destination?.name || '—',
    warehouseCountry: data.destination?.address?.country || 'US',
  };
}

export function getDetailProductImages(
  items: WmsShipmentInboundType['items'],
  limit = 3,
): string[] {
  return (items ?? [])
    .map(i => i.images?.display_image)
    .filter((url): url is string => Boolean(url))
    .slice(0, limit);
}

export function getEffectiveShipmentStatus(
  data: WmsShipmentInboundType,
): ShipmentStatusKey {
  return normalizeShipmentStatus(data.wmsStatus || data.status);
}

export function getInboundShipmentWarehouseId(
  data: WmsShipmentInboundType,
  globalWarehouseId?: string,
): string {
  return data.destination?.wmsMetadata?.wmsWarehouseId || globalWarehouseId || '';
}

export function resolveAliasLineStatus(
  alias: {
    qty?: number;
    received_qty?: number;
    counted?: number;
    status?: string;
    metadata?: { checked_quantity?: number } | null;
  },
  cartonCount: number,
): string {
  if (alias.status) return alias.status;
  const counted =
    alias.metadata?.checked_quantity ?? alias.counted ?? alias.received_qty ?? 0;
  if (counted <= 0) return 'pending';
  if (counted >= cartonCount) return 'stored';
  return 'counted';
}

export {
  canMarkShipmentArrived,
  getInboundWorkflowStepIndex,
  getShipmentItemStatusMeta,
  getShipmentStatusMeta,
  normalizeShipmentStatus,
};
